import React, { useEffect, useState } from 'react';
import axios from 'axios';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const GA_ID_PATTERN = /^G-[A-Z0-9]{4,20}$/i;

const GoogleAnalyticsPanel = ({ isAdmin, userRole }) => {
  const [measurementId, setMeasurementId] = useState('');
  const [persistedId, setPersistedId] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const load = async () => {
      try {
        const res = await axios.get(`${apiUrl}/settings/integrations`, {
          headers: { 'x-auth-token': token },
        });
        const id = res.data?.google_analytics?.measurement_id || '';
        setMeasurementId(id);
        setPersistedId(id);
      } catch (err) {
        const status = err.response?.status;
        const suffix =
          status === 404
            ? ' The integrations API is missing on the server — deploy/pull the backend branch with GET /settings/integrations and restart Node.'
            : status === 401
              ? ' Sign in again; your session may have expired.'
              : status === 403
                ? ` Check your account role (HTTP ${status}).`
                : status
                  ? ` (HTTP ${status})`
                  : '';
        setError(`Failed to load Google Analytics settings.${suffix}`);
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setError('');
    const value = measurementId.trim();
    if (value && !GA_ID_PATTERN.test(value)) {
      setError('Measurement ID must look like G-XXXXXXXX (or be left blank to disable).');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please sign in to save settings.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(
        `${apiUrl}/settings/integrations/google-analytics`,
        { measurement_id: value },
        { headers: { 'x-auth-token': token } }
      );
      const next = res.data?.google_analytics?.measurement_id || '';
      setPersistedId(next);
      setMeasurementId(next);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save Google Analytics settings.');
    } finally {
      setSaving(false);
    }
  };

  const dirty = measurementId.trim() !== persistedId;

  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="border-b border-gray-100 bg-gray-50 px-5 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Google Analytics (GA4)</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Applies to your public storefront website (Next.js site that uses{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">GET /api/website</code>
              ). The staff booking app does not load Google Analytics.
            </p>
          </div>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
              persistedId ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
            }`}
          >
            {loaded ? (persistedId ? 'Active' : 'Not configured') : 'Loading…'}
          </span>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <label htmlFor="ga-measurement-id" className="block text-sm font-medium text-gray-700 mb-1">
            Measurement ID
          </label>
          <input
            id="ga-measurement-id"
            type="text"
            value={measurementId}
            onChange={(e) => setMeasurementId(e.target.value)}
            placeholder="G-XXXXXXXXXX"
            disabled={!isAdmin || saving}
            className="w-full md:w-80 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            autoComplete="off"
            spellCheck="false"
          />
          <p className="mt-1 text-xs text-gray-500">
            Find this in Google Analytics under Admin → Data streams. Leave blank to disable analytics.
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
        )}

        {savedAt > 0 && !error && !dirty && (
          <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            Saved. The storefront site will pick this up on the next request (may take up to about a minute with caching).
          </p>
        )}

        {isAdmin ? (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty || !loaded}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save Google Analytics'}
          </button>
        ) : (
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Only <strong>admin</strong> users can change this setting. Your role is{' '}
            <strong>{userRole || 'unknown'}</strong>.
          </p>
        )}
      </div>
    </section>
  );
};

export default GoogleAnalyticsPanel;
